import { useReducer } from "react";

type EditableState<K extends string> = Record<K, boolean>;

type Action<K extends string> =
  | { type: "TOGGLE"; field: K }
  | { type: "RESET" };

function reducer<K extends string>(
  state: EditableState<K>,
  action: Action<K>
): EditableState<K> {
  switch (action.type) {
    case "TOGGLE":
      return { ...state, [action.field]: !state[action.field] };
    case "RESET":
      // turn all fields to false
      return Object.fromEntries(
        Object.keys(state).map((k) => [k, false])
      ) as EditableState<K>;
    default:
      return state;
  }
}

function useEditableFields<K extends string>(
  keys: K[], // pass keys here
  initial?: Partial<EditableState<K>>
) {
  const initialState = keys.reduce(
    (acc, key) => ({ ...acc, [key]: false }),
    {}
  ) as EditableState<K>;

  const [state, dispatch] = useReducer(reducer<K>, {
    ...initialState,
    ...initial,
  });

  const toggleField = (field: K) => dispatch({ type: "TOGGLE", field });

  const reset = () => dispatch({ type: "RESET" });

  return { editable: state, toggleField, reset };
}

export default useEditableFields;
